import { apiClient } from './api-client';
import type {
  EmailConfig,
  CreateEmailConfigDto,
  UpdateEmailConfigDto,
  EmailTestResult,
} from '@/types/channel';

// Re-export types
export type { EmailConfig, CreateEmailConfigDto, UpdateEmailConfigDto, EmailTestResult };

export const channelService = {
  // Email Configuration CRUD
  async getEmailConfigs(): Promise<EmailConfig[]> {
    const response = await apiClient.get('/channels/email/configs');
    return response.data;
  },

  async getEmailConfig(id: string): Promise<EmailConfig> {
    const response = await apiClient.get(`/channels/email/configs/${id}`);
    return response.data;
  },

  async getDefaultEmailConfig(): Promise<EmailConfig | null> {
    try {
      const response = await apiClient.get('/channels/email/configs/default');
      return response.data;
    } catch (error: any) {
      // Return null for 404 (no default config) - this is expected
      if (error.response?.status === 404) {
        return null;
      }
      // Re-throw other errors
      throw error;
    }
  },

  async createEmailConfig(data: CreateEmailConfigDto): Promise<EmailConfig> {
    const response = await apiClient.post('/channels/email/configs', data);
    return response.data;
  },

  async updateEmailConfig(id: string, data: UpdateEmailConfigDto): Promise<EmailConfig> {
    const response = await apiClient.put(`/channels/email/configs/${id}`, data);
    return response.data;
  },

  async deleteEmailConfig(id: string): Promise<void> {
    await apiClient.delete(`/channels/email/configs/${id}`);
  },

  async setDefaultEmailConfig(id: string): Promise<void> {
    await apiClient.post(`/channels/email/configs/${id}/set-default`);
  },

  async testEmailConfig(id: string): Promise<EmailTestResult> {
    const response = await apiClient.post(`/channels/email/configs/${id}/test`);
    return response.data;
  },

  async initializeMicrosoftGraph(id: string): Promise<{ success: boolean; tokenCreated: boolean; accessTokenObtained: boolean; error?: string }> {
    const response = await apiClient.post(`/channels/email/configs/${id}/microsoft-graph/initialize`);
    return response.data;
  },

  async syncEmailConfig(id: string, force: boolean = false): Promise<{ success: boolean; messagesProcessed: number; syncType: string }> {
    const response = await apiClient.post(`/channels/email/configs/${id}/sync${force ? '?force=true' : ''}`);
    return response.data;
  },

  async syncAndProcessEmails(id: string, force: boolean = false, limit: number = 50): Promise<{
    sync: { success: boolean; messagesProcessed: number; syncType: string };
    processing: { processed: number; failed: number; skipped: number };
  }> {
    const params = new URLSearchParams();
    if (force) params.append('force', 'true');
    if (limit) params.append('limit', limit.toString());
    const response = await apiClient.post(`/channels/email/configs/${id}/sync-and-process?${params.toString()}`);
    return response.data;
  },

  async processPendingEmails(limit: number = 50): Promise<{
    processed: number;
    failed: number;
    skipped: number;
    results: Array<{ emailId: string; success: boolean; error?: string }>;
  }> {
    const response = await apiClient.post(`/channels/email/process-pending${limit ? `?limit=${limit}` : ''}`);
    return response.data;
  },

  // AI Settings
  async getAISettings(): Promise<AISettingsResponse> {
    try {
      const response = await apiClient.get('/channels/email/ai-settings');
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return { hasEmailConfig: false, settings: null };
      }
      throw error;
    }
  },

  async updateAISettings(data: Partial<AISettings>): Promise<AISettings> {
    const response = await apiClient.patch('/channels/email/ai-settings', data);
    return response.data;
  },

  async getEmailConfigAISettings(configId: string): Promise<AISettings> {
    const response = await apiClient.get(`/channels/email/configs/${configId}/ai-settings`);
    return response.data;
  },

  async updateEmailConfigAISettings(configId: string, data: Partial<AISettings>): Promise<AISettings> {
    const response = await apiClient.patch(`/channels/email/configs/${configId}/ai-settings`, data);
    return response.data;
  },

  // Undo send window (per email channel)
  async getUndoSendSettings(configId: string): Promise<UndoSendSettings> {
    const response = await apiClient.get(`/channels/email/configs/${encodeURIComponent(configId)}/undo-send-settings`);
    return response.data;
  },

  /** seconds: 0 = undo off, 5–60 = window, null = use the default. */
  async updateUndoSendSettings(configId: string, seconds: number | null): Promise<UndoSendSettings> {
    const response = await apiClient.patch(
      `/channels/email/configs/${encodeURIComponent(configId)}/undo-send-settings`,
      { seconds },
    );
    return response.data;
  },
};

export interface UndoSendSettings {
  configId: string;
  /** The channel's own window; null = uses the default. 0 = off for this channel. */
  seconds: number | null;
  /** What a reply from this channel gets right now (0 = no undo). */
  effectiveSeconds: number;
  defaultSeconds: number;
  /** Undo send is switched off on the server for every channel. */
  globallyDisabled: boolean;
  minSeconds: number;
  maxSeconds: number;
}

// AI Settings types
export interface AISettings {
  aiEnabled: boolean;
  autoReplyEnabled: boolean;
  autoReplyConfidenceThreshold: number;
  draftMode: 'always' | 'low_confidence' | 'handoff_only';
  defaultTeamId?: string;
  excludePatterns?: string[];
  aiTimeoutSeconds?: number;
}

export interface AISettingsResponse {
  hasEmailConfig: boolean;
  configId?: string;
  settings: AISettings | null;
}

export default channelService;
